/**
 * Vendor-lib URL resolution for widgets that lazy-load a runtime library from a
 * `<script>` tag (echarts, hls.js, xlsx).
 *
 * DEFAULT builds (the fallback contract): `__SERVER_BASE__` is `/` — or is
 * undefined in consumers that never define it — so `SBASE` is `''` (falsy) and
 * `vendorLibUrl` returns the pinned public-CDN URL unchanged. The reference
 * reference deploy keeps loading every lib from jsDelivr, byte-identically.
 *
 * BASE-PORTABLE site builds (`VIEWER_BASE=./` → `__SERVER_BASE__ === './'`):
 * `SBASE` is truthy, so `vendorLibUrl` points at THIS site's own bundle. The URL
 * is resolved relative to the widget's chunk via `import.meta.url`, so it works
 * under ANY mount (`/dashboard/`, `/s/<token>/dashboard/`, `/nas/_viewer/
 * preview/`, …) with zero public-CDN egress. The apps/dashboard vendor-libs
 * plugin places the exact pinned files at `<assetsDir>/vendor/` on these builds.
 *
 * Mirrors the `__SERVER_BASE__`/SBASE gate in apps/dashboard/src/main.ts. The
 * `typeof` guard keeps this module safe in package consumers that don't define
 * `__SERVER_BASE__`: esbuild's `define` substitutes the identifier literally
 * (even inside `typeof`), and where it's left undeclared `typeof` yields
 * `'undefined'` at runtime — no ReferenceError — so those builds take the CDN
 * branch. (The dashboard is in practice the only bundler of these builtins; the
 * plugin apps externalise this package.)
 */
declare const __SERVER_BASE__: string | undefined;

const SBASE =
  typeof __SERVER_BASE__ === 'string' ? __SERVER_BASE__.replace(/\/$/, '') : '';

/**
 * Portable builds resolve `vendor/<file>` next to the widget chunk; default
 * builds return the pinned CDN URL unchanged.
 *
 * `import.meta.url` is read into a local first so vite does NOT recognise this
 * as its `new URL(<literal>, import.meta.url)` asset/glob pattern (which would
 * try to bundle `vendor/**` at build time). We want a plain runtime resolution
 * against the served chunk URL, so the file loads under any mount at runtime.
 */
export function vendorLibUrl(file: string, cdnUrl: string): string {
  if (!SBASE) return cdnUrl;
  const chunkUrl = import.meta.url;
  return new URL(`vendor/${file}`, chunkUrl).href;
}
